import React from "react";
import { makeStyles } from '@mui/styles';
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { Grid } from "@mui/material";
import axios from "../utils/axiosConfig";
import { LoaderProcessingOutlined } from "./LoaderProcessing";

const useStyles = makeStyles({
  root: {
    background: "transparent",
    boxShadow: "none",
  },
  // Listing Layout Styles
  listContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    border: "1px solid #e8e8e8",
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: "2.5fr 1fr 1fr 1.5fr",
    gap: "24px",
    padding: "14px 24px",
    background: "#fafafa",
    borderBottom: "1px solid #e8e8e8",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    '@media (max-width: 900px)': {
      display: "none",
    },
  },
  listHeaderWithAction: {
    gridTemplateColumns: "2fr 1fr 1fr 1.5fr 1fr",
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "2.5fr 1fr 1fr 1.5fr",
    gap: "24px",
    padding: "16px 24px",
    alignItems: "center",
    borderBottom: "1px solid #f0f0f0",
    borderLeft: "3px solid #4caf50",
    background: "linear-gradient(90deg, rgba(76, 175, 80, 0.02) 0%, transparent 100%)",
    transition: "all 0.15s ease",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      background: "linear-gradient(90deg, rgba(76, 175, 80, 0.05) 0%, #fafbfc 100%)",
    },
    '@media (max-width: 900px)': {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "12px",
      padding: "16px 20px",
    },
  },
  listItemWithAction: {
    gridTemplateColumns: "2fr 1fr 1fr 1.5fr 1fr",
  },
  testNameCell: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
    '@media (max-width: 900px)': {
      width: "100%",
    },
  },
  testName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1a1a1a",
    margin: 0,
    lineHeight: 1.4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "default",
  },
  statusCell: {
    display: "flex",
    justifyContent: "center",
    '@media (max-width: 900px)': {
      order: -1,
      justifyContent: "flex-start",
    },
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  statusDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
  },
  resultPublished: {
    background: "rgba(76, 175, 80, 0.08)",
    color: "#2e7d32",
    "& $statusDot": {
      background: "#4caf50",
    },
  },
  resultPending: {
    background: "rgba(255, 152, 0, 0.1)",
    color: "#e65100",
    "& $statusDot": {
      background: "#ff9800",
    },
  },
  infoCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13px",
    color: "#555",
    '@media (max-width: 900px)': {
      width: "100%",
      justifyContent: "flex-start",
    },
  },
  infoIcon: {
    width: "16px",
    height: "16px",
    color: "#888",
    flexShrink: 0,
  },
  infoText: {
    fontWeight: 500,
    color: "#333",
  },
  legendFooter: {
    borderTop: "1px solid #f0f0f0",
    background: "#fafafa",
    padding: "8px 24px",
    '@media (max-width: 900px)': {
      padding: "8px 16px",
    },
  },
  legendHeader: {
    fontSize: "9px",
    fontWeight: 700,
    color: "#bbb",
    textTransform: "uppercase",
    letterSpacing: "0.7px",
    marginBottom: "5px",
  },
  legendGrid: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: "3px 10px",
    alignItems: "start",
  },
  legendBadge: {
    fontSize: "9px",
    fontWeight: 600,
    padding: "1px 6px",
    borderRadius: "20px",
    whiteSpace: "nowrap",
    letterSpacing: "0.2px",
    marginTop: "1px",
  },
  legendNote: {
    fontSize: "11px",
    color: "#999",
    lineHeight: 1.4,
    textAlign: "left",
  },
  actionCell: {
    display: "flex",
    justifyContent: "center",
    '@media (max-width: 900px)': {
      width: "100%",
      justifyContent: "stretch",
    },
  },
  actionButton: {
    borderRadius: "6px",
    textTransform: "none",
    fontWeight: 600,
    fontSize: "13px",
    padding: "8px 20px",
    transition: "all 0.2s ease",
    boxShadow: "none",
    '@media (max-width: 900px)': {
      width: "100%",
    },
  },
  // Empty state
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 24px",
    textAlign: "center",
    background: "#fff",
    borderRadius: "12px",
    border: "1px solid #e8e8e8",
  },
  emptyIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    backgroundColor: "#e8f5e9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
  },
  emptyTitle: {
    margin: "0 0 8px 0",
    fontSize: "17px",
    fontWeight: "600",
    color: "#333",
  },
  emptyText: {
    margin: "0",
    fontSize: "14px",
    color: "#666",
    maxWidth: "300px",
    lineHeight: 1.5,
  },
});

const STATUS_DESCRIPTIONS = [
  { status: "COMPLETED",      color: "#166534", bg: "#dcfce7", note: "Submitted successfully within time." },
  { status: "TIMED OUT",      color: "#b45309", bg: "#fef3c7", note: "Submitted when the time limit was exceeded." },
  { status: "ABORTED",        color: "#b91c1c", bg: "#fee2e2", note: "Submitted with tab switch warnings." },
  { status: "SYSTEM ABORTED", color: "#6b21a8", bg: "#f3e8ff", note: "Auto-terminated due to a system event." },
];

export default function CustomizedTables(props) {
  const classes = useStyles();
  const { tableData, typeButton, buttonName, searchRecord, setManualGenerate, manualGenerate, setCompleted } = props;
  const [loading, setLoading] = React.useState({ index: "", status: false, name: "" });

  const manualGenerateResult = (row, index, e) => {
    setCompleted(false);
    setManualGenerate(true);
    setLoading({ index: index, status: true, name: e.currentTarget.name });
    let examId = row.userExamId;
    axios
      .post("/test/manualGenerateResult", {
        headers: {
          "Access-Control-Allow-Origin": "*",
        },
        id: examId,
      })
      .then((_response) => {
        setManualGenerate(false);
        setCompleted(true);
        setLoading({ index: "", status: false, name: "" });
      })
      .catch((error) => {
        console.log("Error manualGenerateResult", error);
      });
  };

  const hasData = tableData !== undefined && tableData["tableData"] !== undefined;

  const formatDate = (dateString) => {
    if (dateString === "N.A.") return "N.A.";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).replace(/ /g, "-") +
      " at " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  };

  return (
    <Grid item xs={12}>
      <Paper className={classes.root}>
        {hasData ? (
          <div className={classes.listContainer}>
            {/* List Header */}
            <div className={`${classes.listHeader} ${searchRecord === true ? classes.listHeaderWithAction : ''}`}>
              <span>Name</span>
              <span style={{ textAlign: "center" }}>Test Status</span>
              <span style={{ textAlign: "center" }}>Result</span>
              <span style={{ textAlign: "center" }}>Completed</span>
              {searchRecord === true && <span style={{ textAlign: "center" }}>Action</span>}
            </div>

            {buttonName !== "FEEDBACK_SURVEY" &&
              (typeButton === "Completed" || typeButton === "SurveyCompleted" || typeButton === "WorkFlowCompleted") &&
              tableData.tableData.map((row, index) => (
                <div key={`completed-${index}`} className={`${classes.listItem} ${searchRecord === true ? classes.listItemWithAction : ''}`}>
                  <div className={classes.testNameCell}>
                    <h3 className={classes.testName} title={row.name}>{row.name}</h3>
                  </div>

                  <div className={classes.infoCell}>
                    <span className={classes.infoText}>{row.testStatus}</span>
                  </div>

                  <div className={classes.statusCell}>
                    <span className={`${classes.statusBadge} ${row.resultStatus === "Published" ? classes.resultPublished : classes.resultPending}`}>
                      <span className={classes.statusDot}></span>
                      {row.resultStatus}
                    </span>
                  </div>

                  <div className={classes.infoCell}>
                    <svg className={classes.infoIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span className={classes.infoText}>{formatDate(row.resultDate)}</span>
                  </div>

                  {searchRecord === true && (
                    <div className={classes.actionCell}>
                      {loading.status && loading.index === index && loading.name === `manual-regenerate_${buttonName}_${index}` ? (
                        <LoaderProcessingOutlined />
                      ) : (
                        <Button
                          type="submit"
                          variant="outlined"
                          color="primary"
                          disabled={manualGenerate === true}
                          name={`manual-regenerate_${buttonName}_${index}`}
                          className={classes.actionButton}
                          onClick={(e) => manualGenerateResult(row, index, e)}
                        >
                          ReGenerate
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}

            {buttonName === "FEEDBACK_SURVEY" &&
              typeButton !== "Completed" &&
              typeButton !== "SurveyCompleted" &&
              typeButton !== "WorkFlowComplete" &&
              tableData.tableData.map((row, index) => (
                <div key={`feedback-${index}`} className={classes.listItem}>
                  <div className={classes.testNameCell}>
                    <h3 className={classes.testName} title={row.assessee}>{row.assessee}</h3>
                  </div>

                  <div className={classes.infoCell}>
                    <span className={classes.infoText}>{row.testStatus}</span>
                  </div>

                  <div className={classes.statusCell}>
                    <span className={`${classes.statusBadge} ${row.resultStatus === "Published" ? classes.resultPublished : classes.resultPending}`}>
                      <span className={classes.statusDot}></span>
                      {row.resultStatus}
                    </span>
                  </div>

                  <div className={classes.infoCell}>
                    <svg className={classes.infoIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span className={classes.infoText}>{formatDate(row.resultDate)}</span>
                  </div>
                </div>
              ))}
            <div className={classes.legendFooter}>
              <div className={classes.legendHeader}>Status Guide</div>
              <div className={classes.legendGrid}>
                {STATUS_DESCRIPTIONS.map(({ status, color, bg, note }) => (
                  <React.Fragment key={status}>
                    <span className={classes.legendBadge} style={{ color, background: bg }}>{status}</span>
                    <span className={classes.legendNote}>{note}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className={classes.emptyState}>
            <div className={classes.emptyIcon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h4 className={classes.emptyTitle}>
              {typeButton === "FeedbackCompleted"
                ? "No Completed Feedback"
                : typeButton === "SurveyCompleted"
                ? "No Completed Survey"
                : typeButton === "WorkFlowCompleted"
                ? "No Completed WorkFlow"
                : "No Completed Tests"}
            </h4>
            <p className={classes.emptyText}>
              Completed items will appear here.
            </p>
          </div>
        )}
      </Paper>
    </Grid>
  );
}
